import { useField } from 'formik';
import { useMemo, SyntheticEvent } from 'react';
import {
  Autocomplete,
  InputAdornment,
  AutocompleteChangeReason,
} from '@mui/material';

import { controlError } from 'utils';

import { Icon } from '../icons';
import { Input, InputProps } from '../input';

export interface SelectOption {
  label: string;
  value: string | number;
}

export interface AutocompleteFieldProps extends Omit<
  InputProps,
  'name' | 'onChange'
> {
  name: string;
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  onChange?: (event: { target: { name: string; value: string } }) => void;
}

export function AutocompleteField({
  name,
  label,
  options,
  required,
  onChange,
  placeholder = 'Select',
}: AutocompleteFieldProps) {
  const [field, meta] = useField<string | string[]>(name);
  const errorText = controlError(meta, name, label);

  const selectedValue = useMemo(
    () => options.find((option) => option.value === field.value) || null,
    [field.value, options]
  );

  const handleChange = (
    _: SyntheticEvent,
    value: SelectOption | null,
    reason: AutocompleteChangeReason
  ) => {
    let valueToChange: string | string[] | undefined = undefined;

    if (reason === 'selectOption') {
      valueToChange = String((value as SelectOption | null)?.value ?? '');
    }

    if (valueToChange !== undefined) {
      field.onChange({ target: { name, value: valueToChange } });
      onChange?.({ target: { name, value: valueToChange } });
    }
  };

  return (
    <Autocomplete
      fullWidth
      options={options}
      value={selectedValue}
      onChange={handleChange}
      renderInput={(params) => (
        <Input
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end" className="pointer-events-none">
                  <Icon.ChevronDown />
                </InputAdornment>
              ),
            },
          }}
          {...params}
          name={name}
          label={label}
          required={required}
          error={!!errorText}
          onBlur={field.onBlur}
          helperText={errorText}
          placeholder={placeholder}
        />
      )}
    />
  );
}
